/**
 * DomToast — appends toasts directly to document.body.
 * Works on iOS WKWebView / Capacitor where position:fixed inside
 * overflow:hidden ancestors is broken.
 */
export class DomToast {

  private static styleInjected = false;

  /** Inject a <style> block once so CSS classes work (env() only works in real CSS, not inline JS styles) */
  private static injectStyles(): void {
    if (this.styleInjected || document.getElementById('dom-toast-styles')) {
      this.styleInjected = true;
      return;
    }
    this.styleInjected = true;
    const style = document.createElement('style');
    style.id = 'dom-toast-styles';
    style.textContent = `
      #dom-toast-wrap {
        position: fixed !important;
        bottom: calc(env(safe-area-inset-bottom, 0px) + 24px) !important;
        right: 16px;
        left: 16px;
        max-width: 400px;
        margin-left: auto;
        z-index: 2147483647 !important;
        display: flex;
        flex-direction: column-reverse;
        gap: 10px;
        pointer-events: none;
      }
      .dom-toast {
        pointer-events: auto;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px 16px;
        background: rgba(15, 23, 42, 0.98);
        border-radius: 12px;
        box-shadow: 0 4px 24px rgba(0,0,0,0.7), 0 1px 4px rgba(0,0,0,0.5);
        width: 100%;
        box-sizing: border-box;
        opacity: 0;
        transform: translateY(24px);
        transition: opacity 0.3s ease, transform 0.35s ease;
        will-change: opacity, transform;
      }
      .dom-toast.dom-toast--in {
        opacity: 1 !important;
        transform: translateY(0) !important;
      }
      .dom-toast--success {
        border: 1px solid rgba(34, 197, 94, 0.5);
        border-left: 4px solid #22c55e;
      }
      .dom-toast--error {
        border: 1px solid rgba(239, 68, 68, 0.5);
        border-left: 4px solid #ef4444;
      }
      .dom-toast--notify {
        border: 1px solid rgba(56, 189, 248, 0.4);
        border-left: 4px solid #38bdf8;
      }
      .dom-toast__icon {
        font-size: 1.3rem;
        flex-shrink: 0;
        line-height: 1.4;
      }
      .dom-toast__body {
        flex: 1;
        min-width: 0;
      }
      .dom-toast__title {
        font-size: 0.92rem;
        font-weight: 700;
        color: #f0fdf4;
        line-height: 1.3;
      }
      .dom-toast__detail {
        font-size: 0.8rem;
        color: rgba(240,253,244,0.65);
        margin-top: 3px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .dom-toast__close {
        background: none;
        border: none;
        color: rgba(240,253,244,0.5);
        cursor: pointer;
        font-size: 1rem;
        padding: 0;
        flex-shrink: 0;
        align-self: center;
        line-height: 1;
        min-width: 24px;
        text-align: center;
      }
      /* ── Light theme overrides ── */
      body.theme-light .dom-toast {
        background: rgba(255, 255, 255, 0.98);
        box-shadow: 0 4px 24px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.1);
      }
      body.theme-light .dom-toast__title {
        color: #1a1a2e;
      }
      body.theme-light .dom-toast__detail {
        color: rgba(26,26,46,0.6);
      }
      body.theme-light .dom-toast__close {
        color: rgba(26,26,46,0.45);
      }
      body.theme-light .dom-toast--success {
        border: 1px solid rgba(34, 197, 94, 0.45);
        border-left: 4px solid #22c55e;
      }
      body.theme-light .dom-toast--error {
        border: 1px solid rgba(239, 68, 68, 0.45);
        border-left: 4px solid #ef4444;
      }
      body.theme-light .dom-toast--notify {
        border: 1px solid rgba(56, 189, 248, 0.45);
        border-left: 4px solid #38bdf8;
      }
    `;
    document.head.appendChild(style);
  }

  private static getWrap(): HTMLElement {
    let wrap = document.getElementById('dom-toast-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.id = 'dom-toast-wrap';
      document.body.appendChild(wrap);
    }
    return wrap;
  }

  static success(title: string, detail?: string, duration = 4500): void {
    DomToast.show('success', title, detail, duration);
  }

  static error(msg: string, duration = 5500): void {
    DomToast.show('error', msg, undefined, duration);
  }

  /** Bell notification toast — blue accent, slides from bottom like success/error */
  static notify(title: string, body?: string, duration = 5000): void {
    DomToast.show('notify', title, body, duration);
  }

  private static show(
    type: 'success' | 'error' | 'notify',
    title: string,
    detail: string | undefined,
    duration: number
  ): void {
    DomToast.injectStyles();
    const wrap = DomToast.getWrap();
    const icon = type === 'success' ? '✅' : type === 'notify' ? '🔔' : '⚠️';

    // Sanitise: comes from internal logic but good practice
    const safeTitle  = String(title).replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const safeDetail = detail ? String(detail).replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';

    const toast = document.createElement('div');
    toast.className = `dom-toast dom-toast--${type}`;
    toast.innerHTML = `
      <span class="dom-toast__icon">${icon}</span>
      <div class="dom-toast__body">
        <div class="dom-toast__title">${safeTitle}</div>
        ${safeDetail ? `<div class="dom-toast__detail">${safeDetail}</div>` : ''}
      </div>
      <button class="dom-toast__close" data-dismiss>&#10005;</button>
    `;

    wrap.appendChild(toast);

    const dismiss = () => {
      clearTimeout(timer);
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(24px)';
      setTimeout(() => { if (toast.parentNode) { toast.remove(); } }, 350);
    };

    const dismissBtn = toast.querySelector('[data-dismiss]') as HTMLElement;
    if (dismissBtn) { dismissBtn.addEventListener('click', dismiss); }

    // Use setTimeout(30ms) — more reliable than rAF on iOS WKWebView
    const timer = setTimeout(dismiss, duration);
    setTimeout(() => { toast.classList.add('dom-toast--in'); }, 30);
  }
}

